import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Autocomplete,
  Chip,
  Alert,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Avatar,
  ListItemAvatar,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Paper,
  Grid
} from '@mui/material';
import AdminButton from 'components/common/AdminButton';
import {
  Close as CloseIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Warning as WarningIcon,
  AddCircleOutline as AddCircleOutlineIcon
} from '@mui/icons-material';
import { format, parseISO, formatISO } from 'date-fns';
import { useUser } from 'components/RDAuthContext';
import { managerService, roleService } from '../services/domain'; // UPDATED: Using managerService and roleService

function EditLocationManagersModal({ 
  open, 
  onClose, 
  location, 
  onSave,
  existingManagers = []
}) {
  const { user } = useUser();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [managerAssignments, setManagerAssignments] = useState([]);
  const [assignmentReason, setAssignmentReason] = useState('');
  const [reasonForChange, setReasonForChange] = useState(''); // For removals
  const [error, setError] = useState(null);
  const [removedAssignments, setRemovedAssignments] = useState([]); // Track removed managers

  // Load available users and roles when modal opens
  useEffect(() => {
    if (open) {
      loadAvailableData();
      // Reset removed assignments when modal opens
      setRemovedAssignments([]);
      setReasonForChange('');
      // Pre-populate with existing managers
      if (existingManagers && existingManagers.length > 0) {
        setManagerAssignments(existingManagers.map(m => ({
          id: m.id,
          employee: {
            id: m.employeeId || m.id,
            userId: m.userId,
            userName: m.userName,
            firstName: m.firstName,
            lastName: m.lastName,
            email: m.email
          },
          role: {
            id: m.roleDefinitionId || 'STORE_MANAGER',
            roleName: m.roleSnapshot || 'Store Manager'
          },
          isExisting: true,
          effectiveDate: m.effectiveDate,
          assignedBy: m.assignedBy,
          assignedByName: m.assignedByName,
          assignmentReason: m.assignmentReason
        })));
      } else {
        // Start with one empty assignment row for new assignments
        setManagerAssignments([{ employee: null, role: null, isExisting: false }]);
      }
    }
  }, [open, existingManagers]);

  const loadAvailableData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Load both employees and roles in parallel
      const [employees, roles] = await Promise.all([
        managerService.getAvailableManagers(user.clientId || user.client),
        roleService.getLocationRoles(user.clientId || user.client)
      ]);
      
      setAvailableUsers(employees);
      setAvailableRoles(roles);
    } catch (error) {
      console.error('Error loading available data:', error);
      setError('Failed to load available employees or roles. Please try again.');
      setAvailableUsers([]);
      setAvailableRoles([]);
    } finally {
      setLoading(false);
    }
  };

  const handleEmployeeChange = (index, newValue) => {
    const updated = [...managerAssignments];
    updated[index] = { ...updated[index], employee: newValue };
    setManagerAssignments(updated);
  };

  const handleRoleChange = (index, newValue) => {
    const updated = [...managerAssignments];
    updated[index] = { ...updated[index], role: newValue };
    setManagerAssignments(updated);
  };

  const handleAddAssignment = () => {
    setManagerAssignments([...managerAssignments, { employee: null, role: null, isExisting: false }]);
  };

  const handleRemoveAssignment = (index) => {
    const assignmentToRemove = managerAssignments[index];
    if (assignmentToRemove.isExisting) {
      // Track removed existing managers
      setRemovedAssignments([...removedAssignments, assignmentToRemove]);
    }
    setManagerAssignments(managerAssignments.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    // Validate that all assignments have both employee and role selected
    const incompleteAssignments = managerAssignments.filter(
      a => !a.isExisting && (!a.employee || !a.role)
    );
    
    if (incompleteAssignments.length > 0) {
      setError('Please select both an employee and a role for all assignments');
      return;
    }

    // Allow saving with 0 managers if we're only removing
    if (managerAssignments.length === 0 && removedAssignments.length === 0) {
      setError('No changes to save');
      return;
    }

    // Validate reason for new assignments
    if (!assignmentReason.trim() && managerAssignments.some(a => !a.isExisting)) {
      setError('Please provide a reason for the assignment');
      return;
    }

    // Validate reason for removals
    if (!reasonForChange.trim() && removedAssignments.length > 0) {
      setError('Please provide a reason for removing managers');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      // Handle removals first
      if (removedAssignments.length > 0) {
        const managerIdsToRemove = removedAssignments.map(a => a.employee.id);
        await managerService.removeLocationManagers(
          location.id,
          managerIdsToRemove,
          user.clientId || user.client,
          location.locationName || 'Location',
          reasonForChange // Pass the reason for change
        );
      }

      // Then handle additions - only process new managers
      const newAssignments = managerAssignments.filter(a => !a.isExisting && a.employee && a.role);
      if (newAssignments.length > 0) {
        // Debug: Check user object
        console.log('📌 Current user object:', user);
        console.log('📌 User ID:', user?.id || user?.ID || user?.userId);
        
        const currentUserId = user?.id || user?.ID || user?.userId;
        if (!currentUserId) {
          console.error('❌ Unable to determine current user ID from user object:', user);
          setError('Unable to determine current user. Please refresh and try again.');
          setSaving(false);
          return;
        }
        
        const formattedAssignments = newAssignments.map(assignment => ({
          id: assignment.employee.id, // Employee.id
          employeeId: assignment.employee.employeeId || assignment.employee.id, // Employee.id
          userId: assignment.employee.userId, // Reference to User.id if needed
          userName: assignment.employee.userName,
          firstName: assignment.employee.firstName,
          lastName: assignment.employee.lastName,
          email: assignment.employee.email,
          roleDefinitionId: assignment.role.id, // Use actual RoleDefinition.id
          roleSnapshot: assignment.role.roleName, // Store role name snapshot
          effectiveDate: assignment.effectiveDate || formatISO(new Date()),
          assignedBy: assignment.assignedBy || currentUserId,
          assignedByName: assignment.assignedByName || `${user.firstName} ${user.lastName}`.trim() || user.userName,
          assignmentReason: assignment.assignmentReason || assignmentReason,
          isActive: true,
          managementLevel: 'LOCATION_LEVEL'
        }));

        await managerService.updateLocationManagers(
          location.id,
          formattedAssignments,
          user.clientId || user.client,
          location.locationName || 'Location'
        );
      }

      // Call the onSave callback with current state
      if (onSave) {
        // Format assignments back to the expected format
        const formattedManagers = managerAssignments.map(a => ({
          id: a.id || a.employee?.id,
          employeeId: a.employee?.id,
          userId: a.employee?.userId,
          userName: a.employee?.userName,
          firstName: a.employee?.firstName,
          lastName: a.employee?.lastName,
          email: a.employee?.email,
          roleDefinitionId: a.role?.id,
          roleSnapshot: a.role?.roleName,
          effectiveDate: a.effectiveDate,
          assignedBy: a.assignedBy,
          assignedByName: a.assignedByName,
          assignmentReason: a.assignmentReason
        }));
        onSave(formattedManagers);
      }

      // Close the modal
      onClose();

      // Reset state
      setManagerAssignments([]);
      setRemovedAssignments([]);
      setAssignmentReason('');
      setReasonForChange('');
    } catch (error) {
      console.error('❌ Error saving manager assignments:', error);
      console.error('Error details:', JSON.stringify(error, null, 2));
      
      // Extract more specific error message if available
      let errorMessage = 'Failed to save manager assignments. Please try again.';
      if (error.message) {
        errorMessage = `Error: ${error.message}`;
      }
      if (error.errors && error.errors.length > 0) {
        errorMessage = `Error: ${error.errors[0].message}`;
      }
      
      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (!saving) {
      setManagerAssignments([]);
      setRemovedAssignments([]);
      setAssignmentReason('');
      setReasonForChange('');
      setError(null);
      onClose();
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '400px' }
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            Manage Location Managers
          </Typography>
          <IconButton size="small" onClick={handleClose} disabled={saving}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {location?.locationName} {location?.locationNumber && `(#${location.locationNumber})`}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Manager Assignments Section */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle2" fontWeight="600">
              Manager Assignments
            </Typography>
            <AdminButton
              variant="outlined"
              onClick={handleAddAssignment}
              disabled={saving}
              startIcon={<AddCircleOutlineIcon />}
              size="small"
            >
              Add Assignment
            </AdminButton>
          </Box>

          {/* Assignment Rows */}
          {managerAssignments.map((assignment, index) => (
            <Paper key={index} variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={5}>
                  <Autocomplete
                    value={assignment.employee}
                    options={availableUsers.filter(u => 
                      !managerAssignments.some((a, i) => i !== index && a.employee?.id === u.id)
                    )}
                    getOptionLabel={(option) => 
                      `${option.firstName} ${option.lastName}`
                    }
                    renderOption={(props, option) => (
                      <Box component="li" {...props}>
                        <Avatar sx={{ mr: 2, width: 32, height: 32 }}>
                          {option.firstName?.charAt(0) || 'E'}
                        </Avatar>
                        <Box>
                          <Typography variant="body2">
                            {option.firstName} {option.lastName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {option.email}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Employee"
                        placeholder="Select employee..."
                        variant="outlined"
                        size="small"
                      />
                    )}
                    onChange={(event, newValue) => handleEmployeeChange(index, newValue)}
                    loading={loading}
                    disabled={saving || assignment.isExisting}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={5}>
                  <Autocomplete
                    value={assignment.role}
                    options={availableRoles}
                    getOptionLabel={(option) => option.roleName || ''}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Role"
                        placeholder="Select role..."
                        variant="outlined"
                        size="small"
                      />
                    )}
                    onChange={(event, newValue) => handleRoleChange(index, newValue)}
                    loading={loading}
                    disabled={saving || assignment.isExisting}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={2}>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1 }}>
                    {assignment.isExisting && (
                      <Chip 
                        label="Current" 
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    )}
                    <IconButton 
                      onClick={() => handleRemoveAssignment(index)}
                      disabled={saving}
                      size="small"
                      color="error"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Grid>
                {assignment.effectiveDate && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">
                      Effective: {format(parseISO(assignment.effectiveDate), 'MMM d, yyyy')}
                      {assignment.assignedByName && ` • Assigned by: ${assignment.assignedByName}`}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Paper>
          ))}

          {managerAssignments.length === 0 && (
            <Box 
              sx={{ 
                p: 3, 
                textAlign: 'center', 
                border: '1px dashed',
                borderColor: 'divider',
                borderRadius: 1,
                backgroundColor: 'action.hover'
              }}
            >
              <Typography variant="body2" color="text.secondary">
                No managers assigned. Click "Add Assignment" to assign managers.
              </Typography>
            </Box>
          )}

          {/* Reason for Change (when removing managers) */}
          {removedAssignments.length > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="body2" fontWeight="600" gutterBottom>
                Removing {removedAssignments.length} manager(s)
              </Typography>
              <TextField
                fullWidth
                label="Reason for Change"
                value={reasonForChange}
                onChange={(e) => setReasonForChange(e.target.value)}
                placeholder="e.g., Role change, Termination, Reassignment..."
                variant="outlined"
                size="small"
                required
                disabled={saving}
                sx={{ mt: 1 }}
              />
            </Alert>
          )}
        </Box>

      </DialogContent>

      <DialogActions sx={{ flexDirection: 'column', alignItems: 'stretch', p: 2 }}>
        {/* Assignment Reason - moved here above buttons */}
        {managerAssignments.some(a => !a.isExisting) && (
          <TextField
            fullWidth
            label="Reason for Assignment"
            value={assignmentReason}
            onChange={(e) => setAssignmentReason(e.target.value)}
            placeholder="e.g., New hire, Role change, Coverage adjustment..."
            variant="outlined"
            size="small"
            required
            disabled={saving}
            sx={{ mb: 2 }}
          />
        )}
        
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          <AdminButton 
            onClick={handleClose} 
            variant="outlined"
            disabled={saving}
          >
            Cancel
          </AdminButton>
          <AdminButton 
            variant="contained" 
            onClick={handleSave}
            disabled={
              saving || 
              (managerAssignments.length === 0 && removedAssignments.length === 0) ||
              // Check for incomplete assignments (missing employee or role)
              managerAssignments.some(a => !a.employee || !a.role) ||
              // Check for missing assignment reason for new assignments
              (managerAssignments.some(a => !a.isExisting) && !assignmentReason.trim()) ||
              // Check for missing reason for removals
              (removedAssignments.length > 0 && !reasonForChange.trim())
            }
            startIcon={saving && <CircularProgress size={16} />}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </AdminButton>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

export default EditLocationManagersModal;